import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import PublicNav from '@/components/PublicNav';
import Logotype from '@/components/shared/Logotype';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

// ==================== IMMERSIVE VIEWER ====================
function GalleryViewer({ photos, index, onClose, onNavigate }) {
  const photo = photos[index];
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onNavigate(index + 1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [index, photos.length, onClose, onNavigate]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && index < photos.length - 1) {
      onNavigate(index + 1);
    }
    if (isRightSwipe && index > 0) {
      onNavigate(index - 1);
    }
  };

  if (!photo) return null;
  const tagline = photo.tagline || photo.title || photo.caption || '';
  const position = photo.tagline_position || 'below';

  return (
    <div 
      className="fixed inset-0 bg-[#0a0a0f] z-[300] flex flex-col" 
      data-testid="gallery-viewer"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-4">
        <span className="text-white/50 text-xs font-mono tracking-wider">{index + 1} / {photos.length}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors" data-testid="gallery-viewer-close">
          <X size={32} />
        </button>
      </div>
      {/* Navigation arrows - larger and more visible */}
      {index > 0 && (
        <button onClick={() => onNavigate(index - 1)}
          className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 text-white/40 hover:text-white/80 hover:bg-black/40 transition-all">
          <ChevronLeft size={48} strokeWidth={1.5} />
        </button>
      )}
      {index < photos.length - 1 && (
        <button onClick={() => onNavigate(index + 1)}
          className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 text-white/40 hover:text-white/80 hover:bg-black/40 transition-all">
          <ChevronRight size={48} strokeWidth={1.5} />
        </button>
      )}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img src={getImageUrl(photo.url)} alt="" className="max-w-full max-h-full object-contain select-none" draggable="false" />
        {tagline && position === 'superimposed' && (
          <div className="absolute bottom-8 left-0 right-0 text-center px-8">
            <p className="text-white/80 text-sm md:text-base font-light italic" style={{ fontFamily: "'Cormorant Garamond', serif", textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
              {tagline}
            </p>
          </div>
        )}
      </div>
      <div className="px-6 md:px-12 py-4 text-center">
        {tagline && position === 'below' && (
          <p className="text-white/60 text-sm italic mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tagline}</p>
        )}
        <div className="flex items-center justify-center gap-4 text-white/40 text-xs tracking-wider">
          {photo.location && <span className="flex items-center gap-1"><MapPin size={10} />{photo.location}</span>}
          {photo.date_taken && <span>{photo.date_taken}</span>}
        </div>
      </div>
    </div>
  );
}

// ==================== MOSAIC THUMBNAIL ====================
function MosaicThumb({ photo, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative group cursor-pointer overflow-hidden rounded-sm bg-white/[0.03]"
      onClick={onClick} data-testid={`gallery-mosaic-${photo.id}`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <ZLoadingBar accent="#D4537E" loading={true} />
        </div>
      )}
      <img 
        src={getImageUrl(photo.url)} 
        alt="" 
        loading="eager"
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-all duration-700 group-hover:brightness-110 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`} 
      />
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        {(photo.tagline || photo.title) && (
          <p className="text-xs text-white/90 truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {photo.tagline || photo.title}
          </p>
        )}
        {photo.location && (
          <p className="text-[10px] text-white/60 truncate">{photo.location}</p>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN GALLERY ====================
export default function FramedGallery() {
  const [photos, setPhotos] = useState([]);
  const [curatedCollections, setCuratedCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [flowSettings, setFlowSettings] = useState({ name: 'Flow', tagline: 'The current edit', is_public: true });

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      const { data } = await axios.get(`${API}/public/framed/all`);
      setPhotos(data.photos || []);
      setCuratedCollections(data.curated || []);
      if (data.flow_settings) {
        setFlowSettings(data.flow_settings);
        // If Flow is not public and there are curated collections, auto-select the first one
        if (!data.flow_settings.is_public && data.curated && data.curated.length > 0) {
          loadCollection(data.curated[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCollection = async (coll) => {
    try {
      const { data } = await axios.get(`${API}/public/framed/curated/${coll.id}`);
      setActiveCollection(data);
    } catch (err) {
      console.error('Failed to load collection:', err);
    }
  };

  const onNavigate = useCallback((i) => setViewerIndex(i), []);

  const openViewer = (photoList, idx) => {
    setViewerPhotos(photoList);
    setViewerIndex(idx);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <ZLoadingBar accent="#D4537E" loading={true} />
    </div>
  );

  // Get current photos based on selection
  const currentPhotos = activeCollection ? (activeCollection.photos || []) : photos;
  const currentTitle = activeCollection ? activeCollection.title : flowSettings.name;
  const currentTagline = activeCollection ? activeCollection.description : flowSettings.tagline;
  
  // Determine if Flow tab should show
  const showFlowTab = flowSettings.is_public && photos.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-4 md:pt-16 pb-20 md:pb-8" data-testid="framed-gallery">
      <PublicNav />

      {/* Header */}
      <header className="px-6 md:px-12 pt-6 md:pt-8 pb-6">
        <div className="max-w-[1000px] mx-auto flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center mb-1">
              <Logotype app="framed" size="header" />
            </div>
            <p className="text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#b0b4c0', paddingLeft: 42 }}>
              A Life in Technicolor
            </p>
          </div>
          <a href="/labs" className="flex md:hidden items-center gap-2" style={{ textDecoration: 'none', opacity: 0.45 }}>
            <Logotype app="zephyr" size="sidebar" />
          </a>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        {/* Nav Pane - Flow + Collections */}
        <div className="mb-6 border-b border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-1">
            {showFlowTab && (
              <button
                onClick={() => setActiveCollection(null)}
                className={`px-4 pb-3 text-xs font-mono tracking-wider uppercase transition-colors ${
                  !activeCollection
                    ? 'text-[#9B6DD4] border-b border-[#9B6DD4] -mb-px'
                    : 'text-[#6b7084]/60 hover:text-[#6b7084]'
                }`}
                data-testid="gallery-nav-flow"
              >
                {flowSettings.name || 'Flow'}
              </button>
            )}
            {curatedCollections.map(coll => (
              <button
                key={coll.id}
                onClick={() => loadCollection(coll)}
                className={`px-4 pb-3 text-xs font-mono tracking-wider uppercase transition-colors ${
                  activeCollection?.id === coll.id
                    ? 'text-[#9B6DD4] border-b border-[#9B6DD4] -mb-px'
                    : 'text-[#6b7084]/60 hover:text-[#6b7084]'
                }`}
                data-testid={`gallery-nav-coll-${coll.id}`}
              >
                {coll.title}
              </button>
            ))}
          </div>
        </div>

        {/* Current View Header */}
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
              {currentTitle}
            </h2>
            {currentTagline && (
              <p className="italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0', fontSize: '17px' }}>
                {currentTagline}
              </p>
            )}
            <p className="text-xs mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
              {currentPhotos.length} {currentPhotos.length === 1 ? 'photograph' : 'photographs'}
            </p>
          </div>
        </div>

        {/* Photos - Grid View */}
        {currentPhotos.length === 0 ? (
          <div data-testid="gallery-empty">
            <ZEmptyState mark="F" accent="#D4537E" message="No photos yet" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[140px] sm:auto-rows-[180px] lg:auto-rows-[200px] gap-2 md:gap-3 pb-12"
            data-testid="gallery-mosaic-grid">
            {currentPhotos.map((photo, i) => (
              <MosaicThumb key={photo.id} photo={photo} onClick={() => openViewer(currentPhotos, i)} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-12 flex items-center justify-center gap-1">
        <img src="/logo-zephyr.png" alt="Zephyr" style={{ width: 16, height: 16, borderRadius: 4, opacity: 0.18 }} />
        <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 10, fontWeight: 300, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.06)' }}>EPHYR</span>
      </div>

      {/* Viewer */}
      {viewerIndex >= 0 && (
        <GalleryViewer photos={viewerPhotos} index={viewerIndex} onClose={() => setViewerIndex(-1)} onNavigate={onNavigate} />
      )}
    </div>
  );
}
